import { getSession } from 'utils/auth'
import { useSatuEventTracker } from 'utils/satuEvent'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'

import type { SETransformAttrsArgs } from 'utils/satuEvent'
import type { KinerjaStatus } from 'types/kinerja'

const SATU_EVENT_NAME = 'ks_skp_fe_user_action_v2'

type ScreenName =
  | 'KINERJA_KS_HOMEPAGE'
  | 'KINERJA_PLANNING_KS_PRAKTIK_KINERJA_PAGE'
  | 'KINERJA_PLANNING_KS_PENGEMBANGAN_KOMPETENSI_PAGE'
  | 'KINERJA_PLANNING_KS_PERILAKU_KERJA_PAGE'
  | 'KINERJA_PLANNING_KS_PENGAJUAN_POP_UP'
  | 'KINERJA_PLANNING_KS_RANGKUMAN_PAGE'
type EventName =
  | 'CLICK_KINERJA_START_PLANNING'
  | 'CLICK_KINERJA_DRAFT_PLANNING'
  | 'CLICK_KINERJA_PROPOSE_PLANNING'
  | 'CLICK_KINERJA_PLANNING_TO_PENGEMBANGAN_KOMPETENSI'
  | 'CLICK_KINERJA_PLANNING_TO_PERILAKU_KERJA'
  | 'CLICK_KINERJA_PLANNING_TO_RANGKUMAN'
  | 'CLICK_KINERJA_PLANNING_TO_POP_UP_CONFIRMATION'

interface TrackerParams {
  eventName: EventName
  screenName: ScreenName
  kinerjaPlanningId?: string
  kinerjaPlanningStatus?: KinerjaStatus

  attributes?: SETransformAttrsArgs
}

const useSKPKSTrackEvent = () => {
  const { SETrackEvent, SETransformAttrs } = useSatuEventTracker()
  const session = getSession()

  const {
    isEnabled: isMultiPeriodEnabled,
    isHavePeriod,
    periodYear,
    periodNo,
    isFetched,
  } = usePeriodContext()

  const trackEvent = (params: TrackerParams) => {
    const attributes = params.attributes ?? {}

    if (isMultiPeriodEnabled && isHavePeriod && isFetched) {
      attributes['year'] = [periodYear, 'int']
      attributes['period'] = [periodNo, 'int']
    }

    SETrackEvent({
      eventName: SATU_EVENT_NAME,
      eventParams: {
        ...params,
        event_trigger: params.screenName,
        event_state: '',
        event_trace_id: '',
        user_id: session?.user?.id ?? '-',
        attributes: SETransformAttrs(attributes),
      },
      sendDefaultParams: true,
      // we manually send the user data above
      // to handle case where the user is not logged in
      sendUserData: false,
      sendAttributes: false,
    })
  }

  const trackClickStartPlanning = (
    params: Required<Pick<TrackerParams, 'screenName' | 'kinerjaPlanningId'>>
  ) =>
    trackEvent({
      eventName: 'CLICK_KINERJA_START_PLANNING',
      screenName: params.screenName,
      kinerjaPlanningId: params.kinerjaPlanningId,
    })

  const trackClickDraftPlanning = (
    params: Required<
      Pick<
        TrackerParams,
        'screenName' | 'kinerjaPlanningId' | 'kinerjaPlanningStatus'
      >
    >
  ) =>
    trackEvent({
      eventName: 'CLICK_KINERJA_DRAFT_PLANNING',
      screenName: params.screenName,
      kinerjaPlanningId: params.kinerjaPlanningId,
      kinerjaPlanningStatus: params.kinerjaPlanningStatus,
    })

  const trackClickProposePlanning = (
    params: Required<
      Pick<
        TrackerParams,
        'screenName' | 'kinerjaPlanningId' | 'kinerjaPlanningStatus'
      >
    >
  ) =>
    trackEvent({
      eventName: 'CLICK_KINERJA_PROPOSE_PLANNING',
      screenName: params.screenName,
      kinerjaPlanningId: params.kinerjaPlanningId,
      kinerjaPlanningStatus: params.kinerjaPlanningStatus,
    })

  const trackClickProposePlanningConfirmation = (
    params: Required<
      Pick<
        TrackerParams,
        'screenName' | 'kinerjaPlanningId' | 'kinerjaPlanningStatus'
      >
    >
  ) =>
    trackEvent({
      eventName: 'CLICK_KINERJA_PLANNING_TO_POP_UP_CONFIRMATION',
      screenName: params.screenName,
      kinerjaPlanningId: params.kinerjaPlanningId,
      kinerjaPlanningStatus: params.kinerjaPlanningStatus,
    })

  const trackClickToPengembanganKompetensi = (
    params: Required<
      Pick<
        TrackerParams,
        'screenName' | 'kinerjaPlanningId' | 'kinerjaPlanningStatus'
      >
    >
  ) =>
    trackEvent({
      eventName: 'CLICK_KINERJA_PLANNING_TO_PENGEMBANGAN_KOMPETENSI',
      screenName: params.screenName,
      kinerjaPlanningId: params.kinerjaPlanningId,
      kinerjaPlanningStatus: params.kinerjaPlanningStatus,
    })

  const trackClickToPerilakuKerja = (
    params: Required<
      Pick<
        TrackerParams,
        'screenName' | 'kinerjaPlanningId' | 'kinerjaPlanningStatus'
      >
    >
  ) =>
    trackEvent({
      eventName: 'CLICK_KINERJA_PLANNING_TO_PERILAKU_KERJA',
      screenName: params.screenName,
      kinerjaPlanningId: params.kinerjaPlanningId,
      kinerjaPlanningStatus: params.kinerjaPlanningStatus,
    })

  const trackClickToRangkuman = (
    params: Required<
      Pick<
        TrackerParams,
        'screenName' | 'kinerjaPlanningId' | 'kinerjaPlanningStatus'
      >
    >
  ) =>
    trackEvent({
      eventName: 'CLICK_KINERJA_PLANNING_TO_RANGKUMAN',
      screenName: params.screenName,
      kinerjaPlanningId: params.kinerjaPlanningId,
      kinerjaPlanningStatus: params.kinerjaPlanningStatus,
    })

  return {
    trackClickDraftPlanning,
    trackClickProposePlanning,
    trackClickStartPlanning,
    trackClickToPengembanganKompetensi,
    trackClickProposePlanningConfirmation,
    trackClickToPerilakuKerja,
    trackClickToRangkuman,
  }
}

export default useSKPKSTrackEvent
